import React, { useContext } from 'react';
import { ANALYTICS_ACTIONS } from 'js/analytics_v2/actions';
import styled from 'astroturf/react';
import { TextButtonLink } from 'js/design_system/link/TextButtonLink';
import { PeriodControlButton } from 'js/analytics_v2/periodControls';
import { AnalyticsPeriod } from 'js/analytics_v2/types';
import {
  AnalyticsSessionContext,
  useAnalyticsHref,
} from 'js/analytics_v2/navigation';
import {
  analyticsTabs,
  AnalyticsTab,
  analyticsTabUpgradePath,
  analyticsSourcePage,
} from 'js/analytics_v2/tabs';

const Navigation = styled.nav`
  background: var(--background-white, #ffffff);
  border-bottom: 1px solid var(--stroke-light, #dddde2);
`;

const Toolbar = styled.div`
  align-items: center;
  display: flex;
  gap: var(--spacing-sm-24, 24px);
  margin: 0 auto;
  max-width: 1170px;
  padding: var(--spacing-xs-8, 8px) var(--spacing-xs-16, 16px);

  @import 'stylesheets/design_system/breakpoint';
  @include breakpoint(small) {
    &[data-custom-controls='true'] {
      flex-wrap: wrap;
      gap: var(--spacing-xs-8, 8px);
    }
  }
`;

const NavigationLinks = styled.div`
  display: flex;
  flex: 1;
  gap: var(--spacing-xs-12, 12px);
  min-width: 0;
  overflow-x: auto;
  white-space: nowrap;

  @import 'stylesheets/design_system/breakpoint';
  @include breakpoint(small) {
    /* Hide the scrollbar but keep swipe scrolling, like MentionsSubNav. */
    overflow-y: hidden;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }
`;

const NavigationLink = styled(TextButtonLink)`
  border-bottom: 3px solid transparent;
  color: var(--text-primary, #141413);
  flex: 0 0 auto;
  height: 44px;
  padding: 0 var(--spacing-xs-4, 4px);
  text-decoration: none;

  &[aria-current='page'] {
    border-bottom-color: var(--primary-brand, #0645b1);
    color: var(--text-link, #0645b1);
    font-weight: 600;
  }
`;

const Controls = styled.div`
  align-items: center;
  display: flex;
  flex: 0 0 auto;
  gap: var(--spacing-xs-12, 12px);

  @import 'stylesheets/design_system/breakpoint';
  @include breakpoint(small) {
    display: none;

    &[data-mobile-visible='true'] {
      display: flex;
      justify-content: flex-end;
      width: 100%;
    }
  }
`;

const PeriodControls = styled.div`
  display: flex;
`;

const PeriodButton = styled(PeriodControlButton)`
  border-radius: 0;

  &:first-child {
    border-radius: var(--border-radius-radius-md, 8px) 0 0
      var(--border-radius-radius-md, 8px);
  }

  & + & {
    margin-left: -1px;
  }

  &:last-child {
    border-radius: 0 var(--border-radius-radius-md, 8px)
      var(--border-radius-radius-md, 8px) 0;
  }
`;

type Props = {
  activeItem: (typeof analyticsTabs)[number]['label'];
  analyticsBasePath: string;
  period?: AnalyticsPeriod;
  onPeriodChange?: (period: AnalyticsPeriod) => void;
  controls?: React.ReactNode;
  mobileControls?: boolean;
  nonPremium?: boolean;
  onTabClick?: (tab: AnalyticsTab) => void;
};

export const AnalyticsV2NavBar: React.FC<Props> = ({
  activeItem,
  analyticsBasePath,
  period,
  onPeriodChange,
  controls,
  mobileControls = false,
  nonPremium,
  onTabClick,
}) => {
  const href = useAnalyticsHref();
  const session = useContext(AnalyticsSessionContext);
  const needsUpgrade =
    nonPremium ?? session?.context.access_mode === 'non_premium';
  const analyticsPath = (suffix: string) =>
    href(`${analyticsBasePath.replace(/\/$/, '')}${suffix}`);
  const tabPath = (key: AnalyticsTab, path: string): string => {
    const upgrade =
      needsUpgrade &&
      analyticsTabUpgradePath(
        key,
        analyticsSourcePage(window.location.pathname),
      );
    if (upgrade) return key === 'readers' ? href(upgrade) : upgrade;
    return analyticsPath(`/activity/${path}`);
  };

  return (
    <Navigation aria-label="Analytics sections">
      <Toolbar data-custom-controls={mobileControls}>
        <NavigationLinks>
          {analyticsTabs.map(({ label, key, path }) => (
            <NavigationLink
              aria-current={label === activeItem ? 'page' : undefined}
              href={tabPath(key, path)}
              data-analytics-action={
                key === 'readers' ? ANALYTICS_ACTIONS.readersTab : undefined
              }
              inline
              key={key}
              onClick={() => onTabClick?.(key)}
              size="sm"
            >
              {label}
            </NavigationLink>
          ))}
        </NavigationLinks>
        <Controls data-mobile-visible={mobileControls}>
          {controls}
          {period && onPeriodChange && (
            <PeriodControls role="group" aria-label="Analytics period">
              <PeriodButton
                selected={period === '30d'}
                aria-pressed={period === '30d'}
                onClick={() => onPeriodChange('30d')}
                data-analytics-action="select_period"
                data-analytics-value="30d"
              >
                30 days
              </PeriodButton>
              <PeriodButton
                selected={period === '60d'}
                aria-pressed={period === '60d'}
                onClick={() => onPeriodChange('60d')}
                data-analytics-action="select_period"
                data-analytics-value="60d"
              >
                60 days
              </PeriodButton>
            </PeriodControls>
          )}
        </Controls>
      </Toolbar>
    </Navigation>
  );
};
